import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { Box, Typography, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { useDropzone } from 'react-dropzone';

// 스크립트의 파싱 로직을 가져와서 브라우저 환경에 맞게 수정
function parseTextToQuestions(content, setId, subjectId) {
    const questions = [];
    const questionBlocks = content.split(/(?=\n-\s\d+\.\n)/).filter(block => block.trim().length > 0);
    const answerMap = { 'A': 1, 'B': 2, 'C': 3, 'D': 4 };

    for (const block of questionBlocks) {
        const questionNumberMatch = block.match(/-\s(\d+)\./);
        const solutionMatch = block.match(/correct answer is ([A-D])\./);
        const questionTextMatch = block.match(/(?:The correct answer is [A-D]\.\n\n)([\s\S]+?)(?=1\.)/s);
        const option1Match = block.match(/1\.\s([\s\S]*?)(?=2\.)/);
        const option2Match = block.match(/2\.\s([\s\S]*?)(?=3\.)/);
        const option3Match = block.match(/3\.\s([\s\S]*?)(?=4\.)/);
        const option4Match = block.match(/4\.\s([\s\S]*?)(?=Solution)/);
        const correctAnswerLetter = solutionMatch ? solutionMatch[1] : null;
        const correctChoiceNumber = correctAnswerLetter ? answerMap[correctAnswerLetter] : null;

        if (questionNumberMatch && correctChoiceNumber && questionTextMatch && option1Match && option2Match && option3Match && option4Match) {
            questions.push({
                question_number: parseInt(questionNumberMatch[1], 10),
                question_text: questionTextMatch[1].trim(),
                choice_1: option1Match[1].trim(),
                choice_2: option2Match[1].trim(),
                choice_3: option3Match[1].trim(),
                choice_4: option4Match[1].trim(),
                correct_choice: correctChoiceNumber,
                explanation: null,
                set_id: setId,
                subject_id: subjectId,
            });
        }
    }
    return questions;
}


export default function BulkUpload() {
    const [file, setFile] = useState(null);
    const [parsedQuestions, setParsedQuestions] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [sets, setSets] = useState([]);
    const [selectedSubjectId, setSelectedSubjectId] = useState('');
    const [selectedSetId, setSelectedSetId] = useState('');
    const [isParsing, setIsParsing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // 과목 및 세트 목록 가져오기
    useEffect(() => {
        const fetchData = async () => {
            const { data: subjectsData } = await supabase.from('subjects').select('id, name');
            const { data: setsData } = await supabase.from('mbe_sets').select('id, title');
            setSubjects(subjectsData || []);
            setSets(setsData || []);
        };
        fetchData();
    }, []);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
        }
    };
    
    // 파일 파싱 및 미리보기 생성
    const handleParse = useCallback(() => {
        if (!file || !selectedSetId || !selectedSubjectId) {
            alert('파일, 과목, 세트를 모두 선택해주세요.');
            return;
        }
        setIsParsing(true);
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result;
            const questions = parseTextToQuestions(content, selectedSetId, selectedSubjectId);
            setParsedQuestions(questions);
            setIsParsing(false);
        };
        reader.readAsText(file);
    }, [file, selectedSetId, selectedSubjectId]);

    // Supabase에 업로드
    const handleUpload = async () => {
        if (parsedQuestions.length === 0) {
            alert('파싱된 문제가 없습니다. 먼저 파일 파싱을 실행해주세요.');
            return;
        }
        setIsUploading(true);
        const { data, error } = await supabase.from('mbe_questions').insert(parsedQuestions).select();
        setIsUploading(false);

        if (error) {
            alert('업로드 실패: ' + error.message);
        } else {
            alert(`성공적으로 ${data.length}개의 문제를 업로드했습니다.`);
            setParsedQuestions([]);
            setFile(null);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h5">1. 업로드 정보 선택</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                    <InputLabel>과목</InputLabel>
                    <Select value={selectedSubjectId} label="과목" onChange={e => setSelectedSubjectId(e.target.value)}>
                        {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                    </Select>
                </FormControl>
                <FormControl fullWidth>
                    <InputLabel>세트</InputLabel>
                    <Select value={selectedSetId} label="세트" onChange={e => setSelectedSetId(e.target.value)}>
                        {sets.map(s => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            <Typography variant="h5" sx={{ mt: 2 }}>2. 텍스트 파일 선택</Typography>
            <Typography variant="body2" color="text.secondary">
                Kaplan 스크립트에서 사용했던 특정 형식의 텍스트(.md, .txt) 파일만 지원됩니다.
            </Typography>
            <Button variant="outlined" component="label">
                파일 선택
                <input type="file" hidden accept=".txt,.md" onChange={handleFileChange} />
            </Button>
            {file && <Typography>선택된 파일: {file.name}</Typography>}

            <Button onClick={handleParse} disabled={!file || isParsing} variant="contained" sx={{ mt: 1 }}>
                {isParsing ? <CircularProgress size={24} /> : '3. 파일 파싱 및 미리보기'}
            </Button>

            {parsedQuestions.length > 0 && (
                <Box>
                    <Typography variant="h6" sx={{ my: 2 }}>파싱된 문제: {parsedQuestions.length}개</Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow><TableCell>#</TableCell><TableCell>문제 내용</TableCell><TableCell>정답</TableCell></TableRow>
                            </TableHead>
                            <TableBody>
                                {parsedQuestions.map((q, i) => (
                                    <TableRow key={i}><TableCell>{q.question_number}</TableCell><TableCell>{q.question_text.slice(0, 100)}...</TableCell><TableCell>{q.correct_choice}</TableCell></TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Button onClick={handleUpload} disabled={isUploading} variant="contained" color="success" sx={{ mt: 2 }}>
                        {isUploading ? <CircularProgress size={24} /> : '4. 데이터베이스에 업로드'}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
